// IndexedDB cache of upcoming events. This is the source of truth for the
// notification scheduler (see views/app/index.vue and public/sw.js), so that
// reminders keep firing even when the store only holds the currently-viewed
// range or the page was reloaded.
//
// NOTE: the schema here (DB name/version + store names/keyPaths) must stay in
// sync with public/events-db-sw.js, which the service worker uses to read the
// same database.
import { EventI } from '@/store/events/state';
import { expandEvents } from '@/utils/rrule-expander';

const DB_NAME = 'digicalendar';
const DB_VERSION = 1;
const EVENTS_STORE = 'events';
const NOTIFIED_STORE = 'notified';

// Keep events for a while after they end so a late-opening tab can still show a
// missed reminder, then prune them.
const PRUNE_GRACE_SEC = 60 * 60; // 1 hour
// Discard "already notified" bookkeeping after a day.
const NOTIFIED_TTL_SEC = 24 * 60 * 60;

export interface NotifiedRecordI {
  id: EventI['id'];
  at: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(EVENTS_STORE)) {
        const store = db.createObjectStore(EVENTS_STORE, { keyPath: 'id' });
        store.createIndex('startTime', 'startTime', { unique: false });
      }
      if (!db.objectStoreNames.contains(NOTIFIED_STORE)) {
        db.createObjectStore(NOTIFIED_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function promisifyTx(txn: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    txn.addEventListener('complete', () => resolve());
    txn.addEventListener('error', () => reject(txn.error));
    txn.addEventListener('abort', () => reject(txn.error));
  });
}

function getAll<T>(store: IDBObjectStore): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve((req.result || []) as T[]);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Upsert current/future events and prune ones that ended long ago. Only events
 * that are still relevant for reminders are kept.
 */
export async function saveUpcomingEvents(events: EventI[]): Promise<void> {
  if (!events || events.length === 0) return;
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return;
  }

  const nowSec = Math.floor(Date.now() / 1000);
  const cutoff = nowSec - PRUNE_GRACE_SEC;
  const expandWindowEnd = nowSec + (30 * 24 * 60 * 60); // expand for the next 30 days

  const txn = db.transaction(EVENTS_STORE, 'readwrite');
  const store = txn.objectStore(EVENTS_STORE);

  // Prune past events first to avoid duplicates from recurring events.
  const existing = await getAll<EventI>(store);
  existing.forEach((event) => {
    if (!event || event.endTime < cutoff) {
      store.delete(event.id as IDBValidKey);
    }
  });

  const expandedEvents = expandEvents(events, cutoff, expandWindowEnd);

  expandedEvents.forEach((event) => {
    if (event && event.id != null && event.endTime >= cutoff) {
      store.put(JSON.parse(JSON.stringify(event)));
    }
  });

  await promisifyTx(txn);
}

/** All cached events whose start is still in the future. */
export async function getUpcomingEvents(): Promise<EventI[]> {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return [];
  }
  const store = db.transaction(EVENTS_STORE, 'readonly').objectStore(EVENTS_STORE);
  const all = await getAll<EventI>(store);
  const nowMs = Date.now();
  return all.filter((e) => e.startTime * 1000 > nowMs);
}

/**
 * Events due within `leadTimeMs` that have not been notified yet. This is what
 * the reminder scheduler iterates over.
 */
export async function getPendingNotifications(leadTimeMs: number): Promise<EventI[]> {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return [];
  }
  const txn = db.transaction([EVENTS_STORE, NOTIFIED_STORE], 'readonly');
  const events = await getAll<EventI>(txn.objectStore(EVENTS_STORE));
  const notified = await getAll<NotifiedRecordI>(txn.objectStore(NOTIFIED_STORE));
  const notifiedIds = new Set(notified.map((n) => String(n.id)));
  const nowMs = Date.now();

  return events.filter((event) => {
    const diff = event.startTime * 1000 - nowMs;
    return diff > 0 && diff <= leadTimeMs && !notifiedIds.has(String(event.id));
  });
}

/** Mark an event as notified so it is not announced again. */
export async function markNotified(id: EventI['id']): Promise<void> {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return;
  }
  const nowSec = Math.floor(Date.now() / 1000);
  const txn = db.transaction(NOTIFIED_STORE, 'readwrite');
  const store = txn.objectStore(NOTIFIED_STORE);
  store.put({ id, at: nowSec });

  // Opportunistically prune stale notified records.
  const all = await getAll<NotifiedRecordI>(store);
  all.forEach((rec) => {
    if (rec.at < nowSec - NOTIFIED_TTL_SEC) {
      store.delete(rec.id as IDBValidKey);
    }
  });

  await promisifyTx(txn);
}

/** Remove a single event (e.g. after deletion). */
export async function removeEvent(id: EventI['id']): Promise<void> {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return;
  }
  const txn = db.transaction([EVENTS_STORE, NOTIFIED_STORE], 'readwrite');
  txn.objectStore(EVENTS_STORE).delete(id as IDBValidKey);
  txn.objectStore(NOTIFIED_STORE).delete(id as IDBValidKey);
  await promisifyTx(txn);
}

/** Wipe everything (used on logout). */
export async function clearEventsCache(): Promise<void> {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return;
  }
  const txn = db.transaction([EVENTS_STORE, NOTIFIED_STORE], 'readwrite');
  txn.objectStore(EVENTS_STORE).clear();
  txn.objectStore(NOTIFIED_STORE).clear();
  await promisifyTx(txn);
}
